<template>
  <modal 
    name="agreement-filter" 
    class="modal-inner modal-agreement-filter" 
    transition="pop-out" 
    :width="modalWidth" 
    :focus-trap="true" 
    height="auto" 
    :scrollable="true"
  >
    <span class="close-button" @click="hide('agreement-filter')"><BaseIcon icon="times-circle" class="text-white"/></span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      {{ $t('agreements.filter_title') }}
    </div>
    <div class="p-4 pb-4">
      <div class="flex flex-wrap gap-y-1.5">
        <!-- Checkbox Filters -->
        <div class="w-full px-2 py-1">
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-xs text-gray-500 uppercase border-b border-gray-200">
                  <th class="text-left py-1 font-semibold text-gray-700 text-sm normal-case">{{ $t('agreements.filter_options') }}</th>
                  <th class="text-center py-1 font-medium w-16">{{ $t('agreements.filter_yes') }}</th>
                  <th class="text-center py-1 font-medium w-16">{{ $t('agreements.filter_no') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr class="border-b border-gray-100">
                  <td class="py-2 text-gray-700">PDF</td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.pdfOnly === 'yes'" @change="toggleFilter('pdfOnly', 'yes')" class="filter-checkbox" />
                  </td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.pdfOnly === 'no'" @change="toggleFilter('pdfOnly', 'no')" class="filter-checkbox" />
                  </td>
                </tr>
                <tr class="border-b border-gray-100">
                  <td class="py-2 text-gray-700">{{ $t('agreements.invoice_sent') }}</td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.invoiceSent === 'yes'" @change="toggleFilter('invoiceSent', 'yes')" class="filter-checkbox" />
                  </td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.invoiceSent === 'no'" @change="toggleFilter('invoiceSent', 'no')" class="filter-checkbox" />
                  </td>
                </tr>
                <tr class="border-b border-gray-100">
                  <td class="py-2 text-gray-700">{{ $t('agreements.has_alarms') }}</td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.hasAlarms === 'yes'" @change="toggleFilter('hasAlarms', 'yes')" class="filter-checkbox" />
                  </td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.hasAlarms === 'no'" @change="toggleFilter('hasAlarms', 'no')" class="filter-checkbox" />
                  </td>
                </tr>
                <tr>
                  <td class="py-2 text-gray-700">{{ $t('agreements.has_insurance') }}</td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.hasInsurance === 'yes'" @change="toggleFilter('hasInsurance', 'yes')" class="filter-checkbox" />
                  </td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.hasInsurance === 'no'" @change="toggleFilter('hasInsurance', 'no')" class="filter-checkbox" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Warning Filters -->
        <div class="w-full px-2 py-1">
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-xs text-gray-500 uppercase border-b border-gray-200">
                  <th class="text-left py-1 font-semibold text-gray-700 text-sm normal-case">{{ $t('agreements.warning_filters') }}</th>
                  <th class="text-center py-1 font-medium w-16">{{ $t('agreements.filter_yes') }}</th>
                  <th class="text-center py-1 font-medium w-16">{{ $t('agreements.filter_no') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr class="border-b border-gray-100">
                  <td class="py-2 text-gray-700">{{ $t('agreements.warnings.filter_short.invoice_without_pdf') }}</td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.invoiceWithoutPdf === 'yes'" @change="toggleFilter('invoiceWithoutPdf', 'yes')" class="filter-checkbox" />
                  </td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.invoiceWithoutPdf === 'no'" @change="toggleFilter('invoiceWithoutPdf', 'no')" class="filter-checkbox" />
                  </td>
                </tr>
                <tr class="border-b border-gray-100">
                  <td class="py-2 text-gray-700">{{ $t('agreements.warnings.filter_short.without_alarms') }}</td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.withoutAlarms === 'yes'" @change="toggleFilter('withoutAlarms', 'yes')" class="filter-checkbox" />
                  </td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.withoutAlarms === 'no'" @change="toggleFilter('withoutAlarms', 'no')" class="filter-checkbox" />
                  </td>
                </tr>
                <tr class="border-b border-gray-100">
                  <td class="py-2 text-gray-700">{{ $t('agreements.warnings.filter_short.dismissed_past_cancellation_date') }}</td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.dismissedPastCancellation === 'yes'" @change="toggleFilter('dismissedPastCancellation', 'yes')" class="filter-checkbox" />
                  </td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.dismissedPastCancellation === 'no'" @change="toggleFilter('dismissedPastCancellation', 'no')" class="filter-checkbox" />
                  </td>
                </tr>
                <tr>
                  <td class="py-2 text-gray-700">{{ $t('agreements.warnings.filter_short.dismissed_with_devices') }}</td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.dismissedWithDevices === 'yes'" @change="toggleFilter('dismissedWithDevices', 'yes')" class="filter-checkbox" />
                  </td>
                  <td class="text-center py-2">
                    <input type="checkbox" :checked="localFilters.dismissedWithDevices === 'no'" @change="toggleFilter('dismissedWithDevices', 'no')" class="filter-checkbox" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="w-full px-2 py-1">
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div class="text-sm font-semibold text-gray-700 mb-2">{{ $t('agreements.filter_by') }}</div>

            <!-- Status Filter -->
            <div class="mb-3">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t('agreements.status') }}
              </label>
              <select 
                v-model="localFilters.status"
                class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option :value="null">{{ $t('agreements.all_statuses') }}</option>
                <option value="active">{{ $t('agreements.active') }}</option>
                <option value="returned">{{ $t('agreements.returned') }}</option>
                <option value="dismissed">{{ $t('agreements.dismissed') }}</option>
                <option value="extended">{{ $t('agreements.extended') }}</option>
                <option value="pending">{{ $t('agreements.pending') }}</option>
                <option value="redrawn">{{ $t('agreements.redrawn') }}</option>
                <option value="active_old">{{ $t('agreements.active_old') }}</option>
              </select>
            </div>

            <!-- The customer's country: Dutch customers are handled in the Swedish portal
                 (admins and sellers there, utils/countryOrigin) -->
            <div class="mb-3" v-if="canFilterCountry">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t('country_origin.label') }}
              </label>
              <select
                v-model="localFilters.countryOrigin"
                class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option :value="null">{{ $t('country_origin.all_countries') }}</option>
                <option value="se">{{ $t('country_origin.se') }}</option>
                <option value="nl">{{ $t('country_origin.nl') }}</option>
              </select>
            </div>

            <!-- Salesperson Filter -->
            <div class="mb-3">
              <BaseSelect 
                v-model="localFilters.selectedSeller" 
                :options="sellers" 
                nameSelector="name" 
                optionSelector="_id" 
                :field_name="$t('my_agreement_table.seller')" 
                :placeholder="$t('agreements.all_sellers')"
              />
            </div>

            <!-- Reseller Filter -->
            <div class="mb-3">
              <BaseSelect 
                v-model="localFilters.selectedReseller" 
                :options="resellers" 
                nameSelector="name" 
                optionSelector="_id" 
                :field_name="$t('secure_cloud_menu.reseller')" 
                :placeholder="$t('agreements.all_resellers')"
              />
            </div>

            <!-- Customer Filter -->
            <div class="mb-2">
              <BaseInput 
                v-model="localFilters.customerSearch" 
                type="text" 
                :field_name="$t('my_agreement_table.name')" 
                :placeholder="$t('agreements.search_customer')"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-4 px-2">
        <button class="btn-secondary-outline" @click="resetFilters">
          {{ $t('reset') }}
        </button>
        <button class="btn-blue" @click="applyFilters">
          {{ $t('agreements.apply_filters') }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import { canFilterDutch } from "@/utils/countryOrigin";

export default {
  props: {
    filters: {
      type: Object,
      default: () => ({
        pdfOnly: null,
        invoiceSent: null,
        hasAlarms: null,
        hasInsurance: null,
        invoiceWithoutPdf: null,
        withoutAlarms: null,
        dismissedPastCancellation: null,
        dismissedWithDevices: null,
        selectedSeller: null,
        selectedReseller: null,
        customerSearch: '',
        status: null,
        countryOrigin: null
      })
    },
    sellers: {
      type: Array,
      default: () => []
    },
    resellers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localFilters: {
        pdfOnly: null,
        invoiceSent: null,
        hasAlarms: null,
        hasInsurance: null,
        invoiceWithoutPdf: null,
        withoutAlarms: null,
        dismissedPastCancellation: null,
        dismissedWithDevices: null,
        selectedSeller: null,
        selectedReseller: null,
        customerSearch: '',
        status: null,
        countryOrigin: null
      }
    };
  },
  computed: {
    canFilterCountry() {
      return canFilterDutch(this.$store.state.user);
    },
    modalWidth() {
      if (window.innerWidth < 768) return "100%";
      return "600px";
    }
  },
  watch: {
    filters: {
      handler(newFilters) {
        this.localFilters = { ...newFilters };
      },
      deep: true,
      immediate: true
    },
    'localFilters.selectedSeller'(val) {
      if (val) this.localFilters.selectedReseller = null;
    },
    'localFilters.selectedReseller'(val) {
      if (val) this.localFilters.selectedSeller = null;
    }
  },
  methods: {
    hide(name) {
      this.$modal.hide(name);
    },
    toggleFilter(field, value) {
      // If already set to this value, clear it (back to null = all)
      // Otherwise, set to the new value
      this.localFilters[field] = this.localFilters[field] === value ? null : value;
    },
    resetFilters() {
      this.localFilters = {
        pdfOnly: null,
        invoiceSent: null,
        hasAlarms: null,
        hasInsurance: null,
        invoiceWithoutPdf: null,
        withoutAlarms: null,
        dismissedPastCancellation: null,
        dismissedWithDevices: null,
        selectedSeller: null,
        selectedReseller: null,
        customerSearch: '',
        status: null,
        countryOrigin: null
      };
      this.$emit('filter-changed', this.localFilters);
      this.hide('agreement-filter');
    },
    applyFilters() {
      this.$emit('filter-changed', this.localFilters);
      this.hide('agreement-filter');
    }
  }
};
</script>

<style scoped>
.filter-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #3b82f6;
}
.space-y-3 > * + * {
  margin-top: 0.75rem;
}
</style>
